import React from 'react'
import { useTheme } from '../../../components/hooks/useTheme';
import { Divider, FormControlLabel, Radio, RadioGroup } from '@mui/material';

type ExtraAmountProps = {
    isCalendarDayBooking: boolean;
    setIsCalendarDayBooking: (value: boolean) => void;
    manualDiscount: number;
    setManualDiscount: (value: number) => void;
    notIncludeVehicleDamagesInLocationFee: boolean;
    setNotIncludeVehicleDamagesInLocationFee: (value: boolean) => void;
    notIncludeVehicleDamagesInSalesTax: boolean;
    setNotIncludeVehicleDamagesInSalesTax: (value: boolean) => void;
    includeVehicleDamagesInRevenue: boolean;
    setIncludeVehicleDamagesInRevenue: (value: boolean) => void;
    includeExternalChargesInLocationFee: boolean;
    setIncludeExternalChargesInLocationFee: (value: boolean) => void;
    includeExternalChargesInTaxes: boolean;
    setIncludeExternalChargesInTaxes: (value: boolean) => void;
    notIncludeLocationFeeInTaxCalculation: boolean;
    setNotIncludeLocationFeeInTaxCalculation: (value: boolean) => void;
    maxHoursBeforeChargingAnExtraDay: number;
    setMaxHoursBeforeChargingAnExtraDay: (value: number) => void;
    maxHoursBeforeAdditionalChargingAnExtraDay: number;
    setMaxHoursBeforeAdditionalChargingAnExtraDay: (value: number) => void;
    disableAllEmails: boolean;
    setDisableAllEmails: (value: boolean) => void;
}

const ExtraAmount = ({
    isCalendarDayBooking,
    setIsCalendarDayBooking,
    manualDiscount,
    setManualDiscount,
    notIncludeVehicleDamagesInLocationFee,
    setNotIncludeVehicleDamagesInLocationFee,
    notIncludeVehicleDamagesInSalesTax,
    setNotIncludeVehicleDamagesInSalesTax,
    includeVehicleDamagesInRevenue,
    setIncludeVehicleDamagesInRevenue,
    includeExternalChargesInLocationFee,
    setIncludeExternalChargesInLocationFee,
    includeExternalChargesInTaxes,
    setIncludeExternalChargesInTaxes,
    notIncludeLocationFeeInTaxCalculation,
    setNotIncludeLocationFeeInTaxCalculation,
    maxHoursBeforeChargingAnExtraDay,
    setMaxHoursBeforeChargingAnExtraDay,
    maxHoursBeforeAdditionalChargingAnExtraDay,
    setMaxHoursBeforeAdditionalChargingAnExtraDay,
    disableAllEmails,
    setDisableAllEmails
}: ExtraAmountProps) => {
    const { themeStyles } = useTheme();

    return (
        <div className="mt-[1.5rem]">
            <span className={`text-[14px] font-semibold inline-block mb-2 ${themeStyles.main.textHeadingColor}`}>
                Extra Amounts
            </span>

            <Divider className={`w-full ${themeStyles.divider.bg}`} />

            <div className="grid grid-cols-2 gap-x-6 gap-y-2 mt-[1rem]">
                <div>
                    <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Calendar Day Booking?</label>
                    <RadioGroup
                        row
                        value={isCalendarDayBooking ? "Yes" : "No"}
                        onChange={(e) => setIsCalendarDayBooking(e.target.value === "Yes")}
                    >
                        <FormControlLabel
                            value="Yes"
                            control={
                                <Radio
                                    sx={{
                                        color: "gray",
                                        "&.Mui-checked": {
                                            color: "#167AC3",
                                        },
                                        '& .MuiSvgIcon-root': {
                                            fontSize: 14,
                                        },
                                    }}
                                />
                            }
                            label="Yes"
                            sx={{
                                color: themeStyles.form.labelTextColor
                            }}
                        />

                        <FormControlLabel
                            value="No"
                            control={
                                <Radio
                                    sx={{
                                        color: "gray",
                                        "&.Mui-checked": {
                                            color: "#167AC3",
                                        },
                                        '& .MuiSvgIcon-root': {
                                            fontSize: 14,
                                        },
                                    }}
                                />
                            }
                            label="No"
                            sx={{
                                color: themeStyles.form.labelTextColor
                            }}
                        />
                    </RadioGroup>
                </div>

                <div />

                <div>
                    <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Manual Discount</label>
                    <input
                        type="text"
                        inputMode="numeric"
                        className={`w-full px-[8px] py-[6px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                        value={manualDiscount}
                        onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, "");
                            setManualDiscount(value === "" ? 0 : Number(value));
                        }}
                        onKeyDown={(e) => {
                            const allowedKeys = [
                                "Backspace",
                                "Delete",
                                "ArrowLeft",
                                "ArrowRight",
                                "Tab",
                                "Home",
                                "End",
                            ];

                            if (!allowedKeys.includes(e.key) && !/^\d$/.test(e.key)) {
                                e.preventDefault();
                            }
                        }}
                        onPaste={(e) => {
                            const pasted = e.clipboardData.getData("text");

                            if (!/^\d+$/.test(pasted)) {
                                e.preventDefault();
                            }
                        }}
                        onDrop={(e) => e.preventDefault()}
                        autoComplete="off"
                    />
                </div>

                <div />

                <div>
                    <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Don't include Vehicle Damages in Location Fee?</label>
                    <RadioGroup
                        row
                        value={notIncludeVehicleDamagesInLocationFee ? "Yes" : "No"}
                        onChange={(e) => setNotIncludeVehicleDamagesInLocationFee(e.target.value === "Yes")}
                    >
                        <FormControlLabel
                            value="Yes"
                            control={
                                <Radio
                                    sx={{
                                        color: "gray",
                                        "&.Mui-checked": {
                                            color: "#167AC3",
                                        },
                                        '& .MuiSvgIcon-root': {
                                            fontSize: 14,
                                        },
                                    }}
                                />
                            }
                            label="Yes"
                            sx={{
                                color: themeStyles.form.labelTextColor
                            }}
                        />

                        <FormControlLabel
                            value="No"
                            control={
                                <Radio
                                    sx={{
                                        color: "gray",
                                        "&.Mui-checked": {
                                            color: "#167AC3",
                                        },
                                        '& .MuiSvgIcon-root': {
                                            fontSize: 14,
                                        },
                                    }}
                                />
                            }
                            label="No"
                            sx={{
                                color: themeStyles.form.labelTextColor
                            }}
                        />
                    </RadioGroup>
                </div>

                <div>
                    <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Don't include Vehicle Damages in Sales Fee?</label>
                    <RadioGroup
                        row
                        value={notIncludeVehicleDamagesInSalesTax ? "Yes" : "No"}
                        onChange={(e) => setNotIncludeVehicleDamagesInSalesTax(e.target.value === "Yes")}
                    >
                        <FormControlLabel
                            value="Yes"
                            control={
                                <Radio
                                    sx={{
                                        color: "gray",
                                        "&.Mui-checked": {
                                            color: "#167AC3",
                                        },
                                        '& .MuiSvgIcon-root': {
                                            fontSize: 14,
                                        },
                                    }}
                                />
                            }
                            label="Yes"
                            sx={{
                                color: themeStyles.form.labelTextColor
                            }}
                        />

                        <FormControlLabel
                            value="No"
                            control={
                                <Radio
                                    sx={{
                                        color: "gray",
                                        "&.Mui-checked": {
                                            color: "#167AC3",
                                        },
                                        '& .MuiSvgIcon-root': {
                                            fontSize: 14,
                                        },
                                    }}
                                />
                            }
                            label="No"
                            sx={{
                                color: themeStyles.form.labelTextColor
                            }}
                        />
                    </RadioGroup>
                </div>

                <div>
                    <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Include Vehicle Damages in Revenue?</label>
                    <RadioGroup
                        row
                        value={includeVehicleDamagesInRevenue ? "Yes" : "No"}
                        onChange={(e) => setIncludeVehicleDamagesInRevenue(e.target.value === "Yes")}
                    >
                        <FormControlLabel
                            value="Yes"
                            control={
                                <Radio
                                    sx={{
                                        color: "gray",
                                        "&.Mui-checked": {
                                            color: "#167AC3",
                                        },
                                        '& .MuiSvgIcon-root': {
                                            fontSize: 14,
                                        },
                                    }}
                                />
                            }
                            label="Yes"
                            sx={{
                                color: themeStyles.form.labelTextColor
                            }}
                        />

                        <FormControlLabel
                            value="No"
                            control={
                                <Radio
                                    sx={{
                                        color: "gray",
                                        "&.Mui-checked": {
                                            color: "#167AC3",
                                        },
                                        '& .MuiSvgIcon-root': {
                                            fontSize: 14,
                                        },
                                    }}
                                />
                            }
                            label="No"
                            sx={{
                                color: themeStyles.form.labelTextColor
                            }}
                        />
                    </RadioGroup>
                </div>

                <div />

                <div>
                    <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Include External Charges in Location Fee?</label>
                    <RadioGroup
                        row
                        value={includeExternalChargesInLocationFee ? "Yes" : "No"}
                        onChange={(e) => setIncludeExternalChargesInLocationFee(e.target.value === "Yes")}
                    >
                        <FormControlLabel
                            value="Yes"
                            control={
                                <Radio
                                    sx={{
                                        color: "gray",
                                        "&.Mui-checked": {
                                            color: "#167AC3",
                                        },
                                        '& .MuiSvgIcon-root': {
                                            fontSize: 14,
                                        },
                                    }}
                                />
                            }
                            label="Yes"
                            sx={{
                                color: themeStyles.form.labelTextColor
                            }}
                        />

                        <FormControlLabel
                            value="No"
                            control={
                                <Radio
                                    sx={{
                                        color: "gray",
                                        "&.Mui-checked": {
                                            color: "#167AC3",
                                        },
                                        '& .MuiSvgIcon-root': {
                                            fontSize: 14,
                                        },
                                    }}
                                />
                            }
                            label="No"
                            sx={{
                                color: themeStyles.form.labelTextColor
                            }}
                        />
                    </RadioGroup>
                </div>

                <div>
                    <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Include External Charges in Taxes?</label>
                    <RadioGroup
                        row
                        value={includeExternalChargesInTaxes ? "Yes" : "No"}
                        onChange={(e) => setIncludeExternalChargesInTaxes(e.target.value === "Yes")}
                    >
                        <FormControlLabel
                            value="Yes"
                            control={
                                <Radio
                                    sx={{
                                        color: "gray",
                                        "&.Mui-checked": {
                                            color: "#167AC3",
                                        },
                                        '& .MuiSvgIcon-root': {
                                            fontSize: 14,
                                        },
                                    }}
                                />
                            }
                            label="Yes"
                            sx={{
                                color: themeStyles.form.labelTextColor
                            }}
                        />

                        <FormControlLabel
                            value="No"
                            control={
                                <Radio
                                    sx={{
                                        color: "gray",
                                        "&.Mui-checked": {
                                            color: "#167AC3",
                                        },
                                        '& .MuiSvgIcon-root': {
                                            fontSize: 14,
                                        },
                                    }}
                                />
                            }
                            label="No"
                            sx={{
                                color: themeStyles.form.labelTextColor
                            }}
                        />
                    </RadioGroup>
                </div>

                <div>
                    <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Don't include Location Fee in Tax Calculation?</label>
                    <RadioGroup
                        row
                        value={notIncludeLocationFeeInTaxCalculation ? "Yes" : "No"}
                        onChange={(e) => setNotIncludeLocationFeeInTaxCalculation(e.target.value === "Yes")}
                    >
                        <FormControlLabel
                            value="Yes"
                            control={
                                <Radio
                                    sx={{
                                        color: "gray",
                                        "&.Mui-checked": {
                                            color: "#167AC3",
                                        },
                                        '& .MuiSvgIcon-root': {
                                            fontSize: 14,
                                        },
                                    }}
                                />
                            }
                            label="Yes"
                            sx={{
                                color: themeStyles.form.labelTextColor
                            }}
                        />

                        <FormControlLabel
                            value="No"
                            control={
                                <Radio
                                    sx={{
                                        color: "gray",
                                        "&.Mui-checked": {
                                            color: "#167AC3",
                                        },
                                        '& .MuiSvgIcon-root': {
                                            fontSize: 14,
                                        },
                                    }}
                                />
                            }
                            label="No"
                            sx={{
                                color: themeStyles.form.labelTextColor
                            }}
                        />
                    </RadioGroup>
                </div>

                <div />

                <div>
                    <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Max Hours Before Charging an Extra Day</label>
                    <input
                        type="text"
                        inputMode="numeric"
                        className={`w-full px-[8px] py-[6px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                        value={maxHoursBeforeChargingAnExtraDay}
                        onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, "");
                            setMaxHoursBeforeChargingAnExtraDay(value === "" ? 0 : Number(value));
                        }}
                        onKeyDown={(e) => {
                            const allowedKeys = [
                                "Backspace",
                                "Delete",
                                "ArrowLeft",
                                "ArrowRight",
                                "Tab",
                                "Home",
                                "End",
                            ];

                            if (!allowedKeys.includes(e.key) && !/^\d$/.test(e.key)) {
                                e.preventDefault();
                            }
                        }}
                        onPaste={(e) => {
                            const pasted = e.clipboardData.getData("text");

                            if (!/^\d+$/.test(pasted)) {
                                e.preventDefault();
                            }
                        }}
                        onDrop={(e) => e.preventDefault()}
                        autoComplete="off"
                    />
                </div>

                <div>
                    <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Max Hours Before Charging an Extra Day (Additional Charges)</label>
                    <input
                        type="text"
                        inputMode="numeric"
                        className={`w-full px-[8px] py-[6px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                        value={maxHoursBeforeAdditionalChargingAnExtraDay}
                        onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, "");
                            setMaxHoursBeforeAdditionalChargingAnExtraDay(value === "" ? 0 : Number(value));
                        }}
                        onKeyDown={(e) => {
                            const allowedKeys = [
                                "Backspace",
                                "Delete",
                                "ArrowLeft",
                                "ArrowRight",
                                "Tab",
                                "Home",
                                "End",
                            ];

                            if (!allowedKeys.includes(e.key) && !/^\d$/.test(e.key)) {
                                e.preventDefault();
                            }
                        }}
                        onPaste={(e) => {
                            const pasted = e.clipboardData.getData("text");

                            if (!/^\d+$/.test(pasted)) {
                                e.preventDefault();
                            }
                        }}
                        onDrop={(e) => e.preventDefault()}
                        autoComplete="off"
                    />
                </div>

                <div>
                    <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Disable all Emails?</label>
                    <RadioGroup
                        row
                        value={disableAllEmails ? "Yes" : "No"}
                        onChange={(e) => setDisableAllEmails(e.target.value === "Yes")}
                    >
                        <FormControlLabel
                            value="Yes"
                            control={
                                <Radio
                                    sx={{
                                        color: "gray",
                                        "&.Mui-checked": {
                                            color: "#167AC3",
                                        },
                                        '& .MuiSvgIcon-root': {
                                            fontSize: 14,
                                        },
                                    }}
                                />
                            }
                            label="Yes"
                            sx={{
                                color: themeStyles.form.labelTextColor
                            }}
                        />

                        <FormControlLabel
                            value="No"
                            control={
                                <Radio
                                    sx={{
                                        color: "gray",
                                        "&.Mui-checked": {
                                            color: "#167AC3",
                                        },
                                        '& .MuiSvgIcon-root': {
                                            fontSize: 14,
                                        },
                                    }}
                                />
                            }
                            label="No"
                            sx={{
                                color: themeStyles.form.labelTextColor
                            }}
                        />
                    </RadioGroup>
                </div>
            </div>
        </div>
    )
}

export default ExtraAmount